/**
 * Petty Cash Vouchers — the list, filterable by float, movement type, status and date.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, RefreshCw, ChevronLeft, ChevronRight, FileText, Clock,
  CheckCircle2, BookCheck, XCircle, AlertCircle, ArrowDownCircle, ArrowUpCircle, Undo2,
} from 'lucide-react';

import { pettyCashVoucherApi } from '@/api/pettyCashVoucher.api';
import { pettyCashApi } from '@/api/pettyCash.api';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { AppRoutes } from '@/constants/routes';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';

import type { PettyCashFloatListDto } from '@/types/pettyCash.types';
import type {
  PettyCashVoucherListDto, PettyCashVoucherFilterDto, PettyCashMovementType,
} from '@/types/pettyCashVoucher.types';

const STATUS_META: Record<string, { icon: typeof FileText; color: string }> = {
  Draft:              { icon: FileText,     color: 'text-slate-500 dark:text-gray-400' },
  Submitted:          { icon: Clock,        color: 'text-sky-500' },
  PendingApproval:    { icon: Clock,        color: 'text-sky-500' },
  Approved:           { icon: CheckCircle2, color: 'text-blue-500' },
  Posted:             { icon: BookCheck,    color: 'text-emerald-600' },
  Rejected:           { icon: XCircle,      color: 'text-rose-500' },
  Cancelled:          { icon: XCircle,      color: 'text-rose-500' },
};

const MOVEMENT_ICON: Record<PettyCashMovementType, { icon: typeof ArrowDownCircle; color: string }> = {
  TOPUP:   { icon: ArrowDownCircle, color: 'text-emerald-600' },
  PAYMENT: { icon: ArrowUpCircle,   color: 'text-red-600' },
  RETURN:  { icon: Undo2,           color: 'text-amber-600' },
};

const fmt = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

export const PettyCashVoucherListPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(Permissions.PTY_FLOAT_PAY) || hasPermission(Permissions.PTY_FLOAT_TOPUP)
    || hasPermission(Permissions.PTY_FLOAT_RETURN);

  const [items,   setItems]   = useState<PettyCashVoucherListDto[]>([]);
  const [floats,  setFloats]  = useState<PettyCashFloatListDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const pageSize = 20;

  const [filter, setFilter] = useState<PettyCashVoucherFilterDto>({});

  useEffect(() => {
    pettyCashApi.list().then(r => setFloats(r.data?.data ?? [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    pettyCashVoucherApi.getList({ ...filter, pageNumber: page, pageSize })
      .then(r => {
        const d = r.data.data;
        setItems(d?.items ?? []);
        setTotal(d?.totalCount ?? 0);
      })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [filter, page]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="flex h-full flex-col">
      {/* Toolbar */}
      <div className="flex-shrink-0 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-slate-900 dark:text-white">Petty Cash Vouchers</h1>
            <p className="text-xs text-slate-400">{total} voucher{total === 1 ? '' : 's'}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} disabled={loading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 dark:text-gray-400
                         hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
            {canCreate && (
              <button onClick={() => navigate(AppRoutes.PETTY_CASH_VOUCHER_NEW)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700
                           text-white rounded-lg transition-colors font-medium">
                <Plus size={14} /> New Voucher
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select value={filter.floatId ?? ''} onChange={e => { setFilter(f => ({ ...f, floatId: e.target.value || undefined })); setPage(1); }}
            className="h-8 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800
                       px-2 text-xs text-slate-700 dark:text-slate-200">
            <option value="">All floats</option>
            {floats.map(f => <option key={f.id} value={f.id}>{f.floatCode} — {f.floatName}</option>)}
          </select>

          <select value={filter.movementType ?? ''} onChange={e => { setFilter(f => ({ ...f, movementType: (e.target.value || undefined) as PettyCashMovementType })); setPage(1); }}
            className="h-8 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800
                       px-2 text-xs text-slate-700 dark:text-slate-200">
            <option value="">All types</option>
            <option value="TOPUP">Top-up</option>
            <option value="PAYMENT">Payment</option>
            <option value="RETURN">Return</option>
          </select>

          <select value={filter.workflowStatus ?? ''} onChange={e => { setFilter(f => ({ ...f, workflowStatus: e.target.value || undefined })); setPage(1); }}
            className="h-8 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800
                       px-2 text-xs text-slate-700 dark:text-slate-200">
            <option value="">All statuses</option>
            <option value="Draft">Draft</option>
            <option value="Submitted">Submitted</option>
            <option value="Approved">Approved</option>
            <option value="Posted">Posted</option>
            <option value="Rejected">Rejected</option>
            <option value="Cancelled">Cancelled</option>
          </select>

          <input type="date" value={filter.dateFrom ?? ''} onChange={e => { setFilter(f => ({ ...f, dateFrom: e.target.value || undefined })); setPage(1); }}
            className="h-8 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 text-xs text-slate-700 dark:text-slate-200" />
          <span className="text-xs text-slate-400">to</span>
          <input type="date" value={filter.dateTo ?? ''} onChange={e => { setFilter(f => ({ ...f, dateTo: e.target.value || undefined })); setPage(1); }}
            className="h-8 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 text-xs text-slate-700 dark:text-slate-200" />
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-48"><Spinner size="md" /></div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-slate-400">
            <AlertCircle size={28} className="mb-2" />
            <p className="text-sm">No vouchers match these filters.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800/90 backdrop-blur-sm border-b border-slate-200 dark:border-slate-700">
              <tr className="text-left text-xs uppercase text-slate-500 dark:text-gray-400">
                <th className="px-4 py-2.5 font-medium">Voucher</th>
                <th className="px-4 py-2.5 font-medium">Date</th>
                <th className="px-4 py-2.5 font-medium">Float</th>
                <th className="px-4 py-2.5 font-medium">Type</th>
                <th className="px-4 py-2.5 font-medium">Account</th>
                <th className="px-4 py-2.5 font-medium text-right">Amount</th>
                <th className="px-4 py-2.5 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
              {items.map(v => {
                const status = STATUS_META[v.workflowStatus] ?? STATUS_META.Draft;
                const StatusIcon = status.icon;
                const movement = MOVEMENT_ICON[v.movementType];
                const MovementIcon = movement.icon;
                return (
                  <tr key={v.id}
                    onClick={() => navigate(`${AppRoutes.PETTY_CASH_VOUCHERS}/${v.id}`)}
                    className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                    <td className="px-4 py-2.5 font-mono text-xs text-blue-700 dark:text-blue-400">{v.voucherNumber}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-600 dark:text-slate-300">{fmtDate(v.voucherDate)}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-700 dark:text-slate-200">{v.floatCode} — {v.floatName}</td>
                    <td className="px-4 py-2.5">
                      <span className={cn('inline-flex items-center gap-1 text-xs font-medium', movement.color)}>
                        <MovementIcon size={13} /> {v.movementType === 'TOPUP' ? 'Top-up' : v.movementType === 'PAYMENT' ? 'Payment' : 'Return'}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-slate-500 dark:text-gray-400 truncate max-w-[220px]">
                      {v.counterGlAccountName ?? '—'}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono text-xs text-slate-700 dark:text-slate-200">
                      {fmt(v.amount)} {v.currencyCode}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={cn('inline-flex items-center gap-1 text-xs font-medium', status.color)}>
                        <StatusIcon size={13} /> {v.workflowStatus}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {total > pageSize && (
        <div className="flex-shrink-0 flex items-center justify-between border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-6 py-2.5">
          <span className="text-xs text-slate-400">Page {page} of {totalPages}</span>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}
              className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-40">
              <ChevronLeft size={16} />
            </button>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
              className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-40">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
